import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { Info } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BrowseProjectCard } from '@/components/discovery/browse-project-card'
import { BrowseCardGrid } from '@/components/discovery/browse-card-grid'
import { BrowseInspirationGrid } from '@/components/discovery/browse-inspiration-grid'
import { BrowseSearchBar } from '@/components/discovery/browse-search-bar'
import { BrowseViewSettings } from '@/components/discovery/browse-view-settings'
import { CommunityHero } from '@/components/community/community-hero'
import { useBrowseInfinite } from '@/features/discovery/use-browse-infinite'
import { useBrowseMode } from '@/features/discovery/use-browse-mode'
import { useBrowseColumns } from '@/features/discovery/use-browse-columns'
import { useBrowseTileSizes } from '@/features/discovery/use-browse-tile-sizes'
import { useInfiniteScrollSentinel } from '@/features/discovery/use-infinite-scroll-sentinel'
import { useMyFavoritedIds } from '@/features/favorites/use-favorites'
import { useCommunity } from '@/features/communities/use-communities'

const PAGE_SIZE = 20

export const CommunityBrowsePage = () => {
  const { slug } = useParams<{ slug: string }>()
  const { data: community, isLoading: communityLoading } = useCommunity(slug)
  const auth = useAuth()

  const {
    data: pages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBrowseInfinite({ community: slug, size: PAGE_SIZE })

  const items = useMemo(() => pages?.flatMap((p) => p.items) ?? [], [pages])

  const [mode, setMode] = useBrowseMode()
  const [columns, setColumns] = useBrowseColumns()
  const [enabledSizes, toggleSize] = useBrowseTileSizes()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    setExpandedIds(new Set())
  }, [mode, slug])
  const handleToggleExpanded = useCallback((projectId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }, [])

  const projectIds = items.map((p) => p.id)
  const favoritedIds = useMyFavoritedIds(projectIds, auth.isAuthenticated)
  const isFavoritedByMe = (projectId: string) => favoritedIds.data?.has(projectId) ?? false

  const onIntersect = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])
  const sentinelRef = useInfiniteScrollSentinel({
    onIntersect,
    enabled: Boolean(hasNextPage) && !isFetchingNextPage,
  })

  if (!slug) return null

  if (!communityLoading && !community) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Community not found</AlertTitle>
          <AlertDescription>
            <Link to="/c" className="underline underline-offset-2">
              Browse all communities
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-none space-y-4 px-4">
      {community && <CommunityHero community={community} />}

      <div className="sticky top-0 z-30 -mx-4 flex items-center gap-2 bg-muted/30 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex-1">
          <BrowseSearchBar />
        </div>
        <BrowseViewSettings
          mode={mode}
          onModeChange={setMode}
          columns={columns}
          onColumnsChange={setColumns}
          enabledSizes={enabledSizes}
          onToggleSize={toggleSize}
        />
        {community && (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/c/${community.slug}/about`}>About</Link>
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {pages && items.length === 0 && (
        <div className="rounded-md border bg-background p-8 text-center">
          <p className="text-base font-medium">No projects in this community yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Be the first — set a project&apos;s community to /c/{slug} from its editor.
          </p>
        </div>
      )}

      {items.length > 0 && mode === 'list' && (
        <div className="space-y-3">
          {items.map((project) => (
            <BrowseProjectCard
              key={project.id}
              project={project}
              isAuthenticated={auth.isAuthenticated}
              isFavoritedByMe={isFavoritedByMe(project.id)}
            />
          ))}
        </div>
      )}
      {items.length > 0 && mode === 'card' && (
        <BrowseCardGrid
          projects={items}
          isAuthenticated={auth.isAuthenticated}
          isFavoritedByMe={isFavoritedByMe}
          expandedIds={expandedIds}
          onToggleExpanded={handleToggleExpanded}
        />
      )}
      {items.length > 0 && mode === 'inspiration' && (
        <BrowseInspirationGrid
          projects={items}
          isAuthenticated={auth.isAuthenticated}
          isFavoritedByMe={isFavoritedByMe}
          columns={columns}
          enabledSizes={enabledSizes}
          expandedIds={expandedIds}
          onToggleExpanded={handleToggleExpanded}
        />
      )}

      <div ref={sentinelRef} aria-hidden className="py-6 text-center text-xs text-muted-foreground">
        {isFetchingNextPage && 'Loading more…'}
        {!hasNextPage && items.length > 0 && '— end —'}
      </div>
    </div>
  )
}
